import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { RotateCcw, Trash2, Link, Clock, Check, XCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { populationsApi } from '@/lib/api'
import { MemberSearchInput } from '@/components/sharing/MemberSearchInput'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { AudienceExternalInvite, AudienceInviteStatus } from '@/types/components/audiences/audience-sharing'

interface ExternalInvitePanelProps {
  audienceId: string
}

type StatusChipStatus = Exclude<AudienceInviteStatus, 'superseded'>

function StatusChip({ status, expiresAt }: { status: StatusChipStatus; expiresAt?: string | null }) {
  const config: Record<StatusChipStatus, { label: string; className: string; Icon: typeof Clock }> = {
    pending: { label: 'Pending', className: 'text-amber-600 bg-amber-50', Icon: Clock },
    accepted: { label: 'Accepted', className: 'text-emerald-600 bg-emerald-50', Icon: Check },
    revoked: { label: 'Revoked', className: 'text-rose-500 bg-rose-50', Icon: XCircle },
    expired: { label: 'Expired', className: 'text-muted-foreground bg-muted/50', Icon: Clock },
  }
  const { label: baseLabel, className, Icon } = config[status] ?? config.pending

  let label = baseLabel
  if (status === 'pending' && expiresAt) {
    const hoursLeft = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 3_600_000)
    if (hoursLeft > 0) {
      label = `Expires in ${hoursLeft}h`
    }
  }

  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', className)}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  )
}

export function ExternalInvitePanel({ audienceId }: ExternalInvitePanelProps) {
  const { t } = useTranslation('app')
  const [invites, setInvites] = useState<Array<AudienceExternalInvite>>([])
  const [revokeTargetId, setRevokeTargetId] = useState<string | null>(null)
  const [revoking, setRevoking] = useState(false)
  const [resendingId, setResendingId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    populationsApi.listInvites(audienceId)
      .then((data) => { if (!cancelled) setInvites(data) })
      .catch(() => { toast.error(t('audienceActions.loadInvitesFailed')) })
    return () => { cancelled = true }
  }, [audienceId, t])

  const handleInvite = useCallback(async (email: string) => {
    const optimistic: AudienceExternalInvite = {
      id: `optimistic-${Date.now()}`,
      email,
      role: 'collaborator',
      status: 'pending',
      createdAt: new Date().toISOString(),
      expiresAt: null,
    }
    setInvites((prev) => [optimistic, ...prev])
    try {
      const created = await populationsApi.createInvite(audienceId, email)
      setInvites((prev) => prev.map((inv) => inv.id === optimistic.id ? created : inv))
      toast.success(t('audienceActions.inviteSent', { email }))
    } catch (err: unknown) {
      setInvites((prev) => prev.filter((inv) => inv.id !== optimistic.id))
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(msg || t('audienceActions.inviteFailed'))
    }
  }, [audienceId, t])

  const handleResend = useCallback(async (inviteId: string) => {
    setResendingId(inviteId)
    try {
      const updated = await populationsApi.resendInvite(audienceId, inviteId)
      setInvites((prev) => prev.map((inv) => inv.id === inviteId ? updated : inv))
      toast.success(t('audienceActions.inviteResent'))
    } catch {
      toast.error(t('audienceActions.resendFailed'))
    } finally {
      setResendingId(null)
    }
  }, [audienceId, t])

  const handleRevoke = useCallback(async () => {
    if (!revokeTargetId) return
    setRevoking(true)
    try {
      await populationsApi.revokeInvite(audienceId, revokeTargetId)
      setInvites((prev) => prev.map((inv) =>
        inv.id === revokeTargetId ? { ...inv, status: 'revoked' as const } : inv,
      ))
      toast.success(t('audienceActions.inviteRevoked'))
    } catch {
      toast.error(t('audienceActions.revokeFailed'))
    } finally {
      setRevoking(false)
      setRevokeTargetId(null)
    }
  }, [audienceId, revokeTargetId, t])

  const handleCopyLink = useCallback((invite: AudienceExternalInvite) => {
    // id == invite token — construct the accept URL client-side
    const url = `${window.location.origin}/audiences/invites/${invite.id}/accept`
    navigator.clipboard.writeText(url).then(
      () => toast.success(t('audienceActions.linkCopied')),
      () => toast.error(t('sharing.copyFailed')),
    )
  }, [t])

  const handleHideInvite = useCallback(async (inviteId: string) => {
    try {
      await populationsApi.dismissInvite(audienceId, inviteId)
    } catch {
      // Best-effort: still remove from local state even if server call fails
    }
    setInvites((prev) => prev.filter((invite) => invite.id !== inviteId))
  }, [audienceId])

  return (
    <>
      <AlertDialog open={revokeTargetId !== null} onOpenChange={(open) => { if (!open) setRevokeTargetId(null) }}>
        <AlertDialogContent className="rounded-2xl sm:max-w-[400px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('audienceActions.revokeInviteQ')}</AlertDialogTitle>
            <AlertDialogDescription>
              This invite will be deactivated and the recipient won&apos;t be able to accept it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">{t('cancel', { ns: 'common' })}</AlertDialogCancel>
            <button
              type="button"
              data-testid="revoke-invite-confirm"
              onClick={handleRevoke}
              disabled={revoking}
              className="inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-medium bg-destructive text-white hover:bg-destructive/90 transition-colors disabled:opacity-50"
            >
              {revoking ? t('audienceActions.revoking') : t('audienceActions.revoke')}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex flex-col gap-4">
        <MemberSearchInput
          members={[]}
          excludeUserIds={[]}
          onSelect={() => {}}
          allowExternalEmail
          onAddExternalEmail={handleInvite}
          placeholder={t('audienceActions.workEmail')}
          addLabel={t('audienceActions.sendInvite')}
          data-testid="external-invite-input"
        />

        {invites.length > 0 && (
          <div className="flex max-h-48 flex-col gap-1.5 overflow-y-auto pr-1">
            {invites.map((invite) => (
              <div
                key={invite.id}
                data-testid={`invite-row-${invite.id}`}
                className="flex items-center gap-2 py-1.5"
              >
                <div className="flex-1 min-w-0">
                  <span className="block text-sm text-foreground truncate">
                    {invite.email}
                  </span>
                  {invite.status === 'accepted' && invite.orgName && (
                    <span className="block text-xs text-muted-foreground truncate">
                      {invite.orgName}
                    </span>
                  )}
                </div>
                {invite.status !== 'superseded' && (
                  <StatusChip status={invite.status} expiresAt={invite.expiresAt} />
                )}
                {invite.status === 'accepted' && (
                  <button
                    type="button"
                    data-testid={`revoke-accepted-invite-${invite.id}`}
                    onClick={() => setRevokeTargetId(invite.id)}
                    title="Revoke access"
                    className="p-1 text-muted-foreground hover:text-rose-500 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                {invite.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      data-testid={`resend-invite-${invite.id}`}
                      onClick={() => handleResend(invite.id)}
                      disabled={resendingId === invite.id}
                      title="Resend invite"
                      className="p-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      data-testid={`copy-link-${invite.id}`}
                      onClick={() => handleCopyLink(invite)}
                      title="Copy invite link"
                      className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Link className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      data-testid={`revoke-invite-${invite.id}`}
                      onClick={() => setRevokeTargetId(invite.id)}
                      title="Revoke invite"
                      className="p-1 text-muted-foreground hover:text-rose-500 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
                {invite.status === 'expired' && (
                  <button
                    type="button"
                    data-testid={`resend-expired-invite-${invite.id}`}
                    onClick={() => handleResend(invite.id)}
                    disabled={resendingId === invite.id}
                    title="Resend expired invite"
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
                {invite.status === 'revoked' && (
                  <button
                    type="button"
                    data-testid={`hide-invite-${invite.id}`}
                    onClick={() => handleHideInvite(invite.id)}
                    title="Hide revoked invite"
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
